// ProductsSubCalls — /admin/products/:pid/sub-calls/:subPromptId.
// Cross-run browser for one sub-prompt within one product, used
// to spot trends ("is decide.author_hypothesis getting better or
// worse over time on this product?"). Each row is (run, item_key,
// attempt) with collapsed parsed output; click expands.
//
// Backend endpoint:
//   GET /api/v1/admin/products/{pid}/sub-calls/{sub_prompt_id}

import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import {
  EndpointUnavailableError,
  listProductSubCalls,
  type SubCallInvocation,
} from "../api/traceApi";
import { BackendPendingBanner } from "../components/BackendPendingBanner";

export function ProductsSubCalls() {
  const { pid = "", subPromptId = "" } = useParams();
  const [searchParams] = useSearchParams();
  // uid is REQUIRED by the backend (LoadRunsForProduct needs both uid +
  // pid). Source of truth: ?uid=<owner_user_id> in the route. Callers
  // (SubPromptsDetail's "all on this product" link, SubCallRow's
  // "↗ cross-run" link) populate it from the inv.user_id /
  // useParams.uid they already have in scope. Pre-this-fix the
  // backend returned HTTP 400 because uid was absent.
  const uid = searchParams.get("uid") ?? "";
  const { data: me } = useUser();
  const [openIdx, setOpenIdx] = useState<number | undefined>();

  const q = useQuery({
    queryKey: ["admin", "product-sub-calls", uid, pid, subPromptId],
    queryFn: () => listProductSubCalls(uid, pid, subPromptId),
    retry: (n, err) => !(err instanceof EndpointUnavailableError) && n < 2,
    enabled: !!pid && !!subPromptId && !!uid,
  });

  if (!me) return null;

  const rows = q.data?.invocations ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <Link
          to={`/admin/sub-prompts/${encodeURIComponent(subPromptId)}`}
          className="font-mono text-[11px] text-ink-400 hover:text-ink-100"
        >
          ← {subPromptId}
        </Link>
        <h1 className="mt-4 text-display font-medium text-ink-50 tracking-tightest mb-2">
          Cross-run sub-calls
        </h1>
        <p className="font-mono text-[11px] text-ink-400 mb-6">
          product {pid} · sub-prompt {subPromptId}
          {uid && <> · uid {uid}</>}
        </p>

        {!uid && (
          <div className="border border-amber-700 bg-amber-950/30 rounded p-3 mb-3 text-sm text-amber-100">
            Missing <code className="font-mono">?uid=</code> query param. Backend
            requires the owner's user ID to scan the product's runs. Open this
            page via a "cross-run" link from a run-trace row (which supplies uid
            automatically), or append <code className="font-mono">?uid=u_…</code>
            to the URL.
          </div>
        )}

        {q.error instanceof EndpointUnavailableError && (
          <BackendPendingBanner
            feature="Cross-run sub-call browser"
            endpoint={`GET /api/v1/admin/products/${pid}/sub-calls/${subPromptId}`}
          />
        )}
        {q.error && !(q.error instanceof EndpointUnavailableError) && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}
        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}

        <ul className="space-y-1">
          {rows.map((r, i) => (
            <Row
              key={i}
              inv={r}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx((o) => (o === i ? undefined : i))}
            />
          ))}
        </ul>
        {!q.isLoading && !q.error && rows.length === 0 && (
          <p className="text-sm text-ink-400">No invocations.</p>
        )}
      </main>
    </div>
  );
}

function Row({
  inv,
  isOpen,
  onToggle,
}: {
  inv: SubCallInvocation;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-3 py-1.5 px-2 hover:bg-ink-900/40 rounded font-mono text-[11px]"
      >
        <span className="w-4 text-ink-400">{isOpen ? "−" : "+"}</span>
        <span className="text-ink-400 w-36 truncate">
          {inv.started_at?.slice(0, 19) ?? "—"}
        </span>
        <span className="text-ink-100 w-32 truncate">
          {inv.run_id?.slice(0, 12) ?? "—"}…
        </span>
        <span className="text-ink-300 w-24 truncate">
          {inv.item_key ?? "—"}
        </span>
        <span className="text-ink-400 w-20">
          attempt {inv.attempt ?? "?"}
        </span>
        <span className="text-ink-300 w-24">{inv.outcome ?? "—"}</span>
        <span className="text-ink-400 ml-auto">
          {inv.duration_ms !== undefined ? `${inv.duration_ms}ms` : "—"}
        </span>
      </button>
      {isOpen && (
        <pre className="text-[10px] font-mono bg-ink-950 border border-ink-800 rounded p-2 overflow-auto max-h-[40vh] mt-1 ml-6">
          {inv.parsed_output
            ? JSON.stringify(inv.parsed_output, null, 2)
            : "(no parsed output)"}
        </pre>
      )}
    </li>
  );
}
